import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import type { z } from "zod";
import { CreateStudentGroupSchema } from "@/modules/master/domain/contracts";
import { isDevMode } from "@/shared/lib/config";

const StudentGroupFormSchema = CreateStudentGroupSchema;
export type StudentGroupFormValues = z.infer<typeof StudentGroupFormSchema>;

export function StudentGroupForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<StudentGroupFormValues>;
  onSubmit: (values: StudentGroupFormValues) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<StudentGroupFormValues>({
    resolver: zodResolver(StudentGroupFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      order: initialValues?.order ?? undefined,
      status: initialValues?.status || "active",
    },
  });

  const handleAutoFill = () => {
    // Mock data for dev mode
    methods.reset({
      name: "ກຸ່ມນັກຮຽນ 1",
      code: "G1",
      description: "ກຸ່ມນັກຮຽນທົ່ວໄປ",
      status: "active",
    });
  };

  return (
    <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-4">
        <FormInput name="name" label="ຊື່" placeholder="ປ້ອນຊື່ກຸ່ມນັກຮຽນ" required />

        <FormInput
          name="code"
          label="Code/Prefix"
          placeholder="ປ້ອນ Code (ຕົວອັກສອນໃຫຍ່, ຕົວເລກ, _ ຫຼື -)"
          required
          hint="Code ນີ້ຈະຖືກໃຊ້ເປັນ prefix ໃນ Member ID (ຕົວຢ່າງ: G1-000001)"
        />

        <FormTextarea
          name="description"
          label="ຄຳອະທິບາຍ"
          placeholder="ປ້ອນຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          rows={3}
        />

        <FormNumber
          name="order"
          label="ລຳດັບ"
          placeholder="ປ້ອນລຳດັບ (ບໍ່ບັງຄັບ)"
          hint="ລຳດັບສຳລັບການຈັດລຽງ (ຖ້າບໍ່ປ້ອນຈະຖືກກຳນົດໂດຍອັດຕະໂນມັດ)"
        />

        <FormSelect
          label="ສະຖານະ"
          name="status"
          options={[
            { value: "active", label: "ໃຊ້ງານ" },
            { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
          ]}
        />
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
